/***
*
*   FEATURES PANEL
*   Manage available features that can be assigned to plans
*
**********/

import { useState, useEffect } from 'react';
import { Card, Button, Icon } from 'components/lib';
import Style from './bundlePackaging.tailwind.js';

export function FeaturesPanel({ features, onSave, onDelete, loading }) {
  const [featuresList, setFeaturesList] = useState([]);
  const [editingFeature, setEditingFeature] = useState(null);
  const [isNewFeature, setIsNewFeature] = useState(false);

  useEffect(() => {
    if (features) {
      setFeaturesList(features);
    }
  }, [features]);

  const handleAddFeature = () => {
    setEditingFeature({
      key: '',
      name: '',
      helpText: '',
      hasLimit: false,
      resetInterval: false,
      // unit: ''
    });
    setIsNewFeature(true);
  };

  const handleEditFeature = (feature) => {
    setEditingFeature({ ...feature });
    setIsNewFeature(false);
  };

  const handleSaveFeature = () => {
    if (!editingFeature.key || !editingFeature.name) {
      return;
    }
    onSave(editingFeature, isNewFeature);
    setEditingFeature(null);
    setIsNewFeature(false);
  };

  const handleCancelEdit = () => {
    setEditingFeature(null);
    setIsNewFeature(false);
  };

  const handleDeleteFeature = (featureKey) => {
    onDelete(featureKey);
  };

  const handleFeatureChange = (field, value) => {
    setEditingFeature(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="space-y-6">
      {/* Add Feature Button */}
      <div className="flex justify-end">
        <Button
          text="Add New Feature"
          icon="plus"
          color="green"
          action={handleAddFeature}
        />
      </div>

      {/* Feature Editor Modal */}
      {editingFeature && (
        <Card>
          <div className={Style.settingsSection}>
            <h3 className={Style.settingsHeader}>
              {isNewFeature ? 'Add New Feature' : 'Edit Feature'}
            </h3>
            <div className={Style.settingsGrid}>
              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Feature Key *</label>
                <input
                  type="text"
                  className={Style.settingsInput}
                  value={editingFeature.key}
                  onChange={(e) => handleFeatureChange('key', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                  placeholder="e.g., manage_people"
                  disabled={!isNewFeature}
                />
                <p className="text-xs text-slate-500 mt-1">Unique identifier (snake_case)</p>
              </div>
              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Feature Name *</label>
                <input
                  type="text"
                  className={Style.settingsInput}
                  value={editingFeature.name}
                  onChange={(e) => handleFeatureChange('name', e.target.value)}
                  placeholder="e.g., Manage People"
                />
              </div>
              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Feature Help Text *</label>
                <input
                  type="text"
                  className={Style.settingsInput}
                  value={editingFeature.helpText || ''}
                  onChange={(e) => handleFeatureChange('helpText', e.target.value)}
                  placeholder="e.g., Manage People"
                />
              </div>
              <div className={Style.settingsCard}>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingFeature.hasLimit || false}
                    onChange={(e) => handleFeatureChange('hasLimit', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className={Style.settingsLabel}>Has Limit</span>
                </label>
                <p className="text-xs text-slate-500 mt-1">Enable if this feature has usage limits</p>
              </div>
              <div className={Style.settingsCard}>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingFeature.resetInterval || false}
                    onChange={(e) => handleFeatureChange('resetInterval', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className={Style.settingsLabel}>Has Reset Interval</span>
                </label>
                <p className="text-xs text-slate-500 mt-1">Enable if limits reset periodically</p>
              </div>
              {/* {editingFeature.hasLimit && (
                <div className={Style.settingsCard}>
                  <label className={Style.settingsLabel}>Unit (optional)</label>
                  <input
                    type="text"
                    className={Style.settingsInput}
                    value={editingFeature.unit || ''}
                    onChange={(e) => handleFeatureChange('unit', e.target.value)}
                    placeholder="e.g., MB, GB, users"
                  />
                </div>
              )} */}
            </div>
            <div className="mt-4 flex gap-3">
              <Button
                text="Cancel"
                color="gray"
                outline
                action={handleCancelEdit}
              />
              <Button
                text={isNewFeature ? 'Create Feature' : 'Save Changes'}
                color="green"
                loading={loading}
                action={handleSaveFeature}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Features List */}
      <div className={Style.featuresGrid}>
        {featuresList.length > 0 ? (
          featuresList.map((feature) => (
            <Card key={feature.key} className={Style.featureCard}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className={Style.featureCardName}>{feature.name}</h3>
                  <p className={Style.featureCardKey}>{feature.key}</p>
                  <p className={Style.featureCardHelp}>{feature.helpText}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    icon="edit-2"
                    size="small"
                    color="blue"
                    outline
                    action={() => handleEditFeature(feature)}
                  />
                  <Button
                    icon="trash-2"
                    size="small"
                    color="red"
                    outline
                    action={() => handleDeleteFeature(feature.key)}
                  />
                </div>
              </div>
              <div className={Style.featureCardMeta}>
                <div className={Style.featureCardTag}>
                  <Icon
                    image={feature.hasLimit ? 'check-circle' : 'x-circle'}
                    color={feature.hasLimit ? 'green' : 'gray'}
                    size={14}
                  />
                  <span>Has Limit</span>
                </div>
                <div className={Style.featureCardTag}>
                  <Icon
                    image={feature.resetInterval ? 'check-circle' : 'x-circle'}
                    color={feature.resetInterval ? 'green' : 'gray'}
                    size={14}
                  />
                  <span>Reset Interval</span>
                </div>
                {feature.unit && (
                  <div className={Style.featureCardTag}>
                    <Icon image="hash" color="blue" size={14} />
                    <span>Unit: {feature.unit}</span>
                  </div>
                )}
              </div>
            </Card>
          ))
        ) : (
          <Card>
            <div className={Style.emptyState}>
              <p className={Style.emptyStateText}>No features configured yet</p>
              <Button
                text="Add Your First Feature"
                icon="plus"
                color="green"
                action={handleAddFeature}
              />
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}